import { useState, useEffect, useRef, useMemo } from 'react'
import { profileUrl } from '../../api/tmdb'
import { updateParty } from '../../utils/partyGame'
import Filmstrip from '../Filmstrip'
import { MODE_NAMES, MODE_ICONS } from './modeMeta'
import {
  IconArrowRight, IconClock, IconCheck, IconX, IconCrown, IconReplay,
} from '../icons'

const TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes

function fmtTime(secs) {
  if (secs == null) return '--:--'
  const m = String(Math.floor(secs / 60)).padStart(2, '0')
  const s = String(secs % 60).padStart(2, '0')
  return `${m}:${s}`
}

const CONFETTI_COLORS = ['#C9A84C', '#debb5a', '#fff6c0', '#9a7a34', '#e8c96a', '#f0d878']

function MiniConfetti() {
  const particles = useMemo(() =>
    Array.from({ length: 35 }, (_, i) => ({
      id: i,
      left: `${(Math.random() * 100).toFixed(1)}%`,
      delay: `${(Math.random() * 2).toFixed(2)}s`,
      duration: `${(1.6 + Math.random() * 1.4).toFixed(2)}s`,
      width: `${(4 + Math.random() * 6).toFixed(1)}px`,
      height: `${(7 + Math.random() * 8).toFixed(1)}px`,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      endRotate: `${Math.floor(Math.random() * 900 + 360)}deg`,
      isCircle: Math.random() > 0.55,
    }))
  , [])

  return (
    <div className="confetti" aria-hidden="true">
      {particles.map((p) => (
        <div
          key={p.id}
          className="confetti__piece"
          style={{
            left: p.left,
            animationDelay: p.delay,
            animationDuration: p.duration,
            width: p.width,
            height: p.isCircle ? p.width : p.height,
            background: p.color,
            borderRadius: p.isCircle ? '50%' : '2px',
            '--end-rotate': p.endRotate,
          }}
        />
      ))}
    </div>
  )
}

// Score formatter — Speed Run shows time only, Battle Royale shows hops · time.
function formatStat(player, isSpeedRun) {
  if (isSpeedRun) return fmtTime(player.time)
  return `${player.hops} hop${player.hops !== 1 ? 's' : ''} · ${fmtTime(player.time)}`
}

/**
 * Party Battle Royale / Speed Run — TV/host display.
 * Same component serves both modes; Speed Run shows time-only, Battle Royale
 * shows hops · time. Live status board during gameplay, ranked results when
 * everyone finishes (or the 5-minute timeout fires).
 */
export default function PartyBattleScreen({ party, onPlayAgain, onBackToLobby, onEndParty }) {
  const [elapsed, setElapsed] = useState(0)
  const timeoutFiredRef = useRef(false)
  const [expandedChain, setExpandedChain] = useState(null) // uid whose chain is expanded

  const isResults  = party?.status === 'results'
  const isSpeedRun = party?.mode === 'speed-run'
  const ModeIcon   = MODE_ICONS[party?.mode]
  const players = party?.players ?? {}
  const playerCount = Object.keys(players).length

  // Count finished + dnf
  const finishedCount = Object.values(players).filter(
    (p) => p.status === 'finished' || p.status === 'dnf'
  ).length

  // Timer tick
  useEffect(() => {
    if (isResults) return
    const startedAt = party?.playingStartedAt
    if (!startedAt) return
    const startMs = typeof startedAt.toMillis === 'function'
      ? startedAt.toMillis()
      : (startedAt.seconds ? startedAt.seconds * 1000 : Date.now())

    const tick = () => setElapsed(Math.floor((Date.now() - startMs) / 1000))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [party?.playingStartedAt, isResults])

  // Auto-transition: all finished
  useEffect(() => {
    if (isResults || playerCount === 0) return
    if (finishedCount >= playerCount && !timeoutFiredRef.current) {
      timeoutFiredRef.current = true
      updateParty(party.id, { status: 'results' }).catch(() => {})
    }
  }, [finishedCount, playerCount, isResults, party?.id])

  // Auto-transition: 5-minute timeout
  useEffect(() => {
    if (isResults || timeoutFiredRef.current) return
    const startedAt = party?.playingStartedAt
    if (!startedAt) return
    const startMs = typeof startedAt.toMillis === 'function'
      ? startedAt.toMillis()
      : (startedAt.seconds ? startedAt.seconds * 1000 : Date.now())

    const remaining = TIMEOUT_MS - (Date.now() - startMs)
    if (remaining <= 0) {
      timeoutFiredRef.current = true
      const updates = { status: 'results' }
      for (const [uid, p] of Object.entries(players)) {
        if (p.status === 'playing' || !p.status) {
          updates[`players.${uid}.status`] = 'dnf'
        }
      }
      updateParty(party.id, updates).catch(() => {})
      return
    }
    const id = setTimeout(() => {
      if (timeoutFiredRef.current) return
      timeoutFiredRef.current = true
      const updates = { status: 'results' }
      for (const [uid, p] of Object.entries(players)) {
        if (p.status === 'playing' || !p.status) {
          updates[`players.${uid}.status`] = 'dnf'
        }
      }
      updateParty(party.id, updates).catch(() => {})
    }, remaining)
    return () => clearTimeout(id)
  }, [party?.playingStartedAt, isResults, party?.id, players])

  // ── Build status board ────────────────────────────────────────────────────
  // Finished players at top in finish order, then still-playing, then DNF.
  const finished = Object.entries(players)
    .filter(([, p]) => p.status === 'finished')
    .sort((a, b) => {
      // Always sort by finishedAt first (true finish order)
      const aTime = a[1].finishedAt?.seconds ?? a[1].finishedAt?.toMillis?.() ?? 9999999999
      const bTime = b[1].finishedAt?.seconds ?? b[1].finishedAt?.toMillis?.() ?? 9999999999
      if (aTime !== bTime) return aTime - bTime
      // Speed Run: finish order is decisive — fall back to play-time only
      if (isSpeedRun) return (a[1].time ?? 9999) - (b[1].time ?? 9999)
      // Battle Royale tiebreaker: hops then time
      const diff = (a[1].hops ?? 999) - (b[1].hops ?? 999)
      if (diff !== 0) return diff
      return (a[1].time ?? 9999) - (b[1].time ?? 9999)
    })

  const stillPlaying = Object.entries(players)
    .filter(([, p]) => p.status === 'playing' || !p.status)
  const dnf = Object.entries(players)
    .filter(([, p]) => p.status === 'dnf')

  const statusBoard = [...finished, ...stillPlaying, ...dnf]

  // ── Rank finished players for results ─────────────────────────────────────
  // Speed Run: rank by finish order (each finisher gets a unique rank).
  // Battle Royale: ties allowed when hops AND time both match.
  function rankFinished(entries) {
    if (isSpeedRun) {
      return entries.map(([uid, p], i) => [uid, p, i + 1])
    }
    let rank = 0, lastHops = -1, lastTime = -1
    return entries.map(([uid, p]) => {
      if (p.hops !== lastHops || p.time !== lastTime) {
        rank += 1
        lastHops = p.hops
        lastTime = p.time
      }
      return [uid, p, rank]
    })
  }

  // ── Results view ──────────────────────────────────────────────────────────
  if (isResults) {
    // Sort for results: Speed Run by finish order; Battle Royale by hops then time
    const finishedSorted = Object.entries(players)
      .filter(([, p]) => p.status === 'finished')
      .sort((a, b) => {
        if (isSpeedRun) {
          const aT = a[1].finishedAt?.seconds ?? a[1].finishedAt?.toMillis?.() ?? 9999999999
          const bT = b[1].finishedAt?.seconds ?? b[1].finishedAt?.toMillis?.() ?? 9999999999
          if (aT !== bT) return aT - bT
          return (a[1].time ?? 9999) - (b[1].time ?? 9999)
        }
        const diff = (a[1].hops ?? 999) - (b[1].hops ?? 999)
        if (diff !== 0) return diff
        return (a[1].time ?? 9999) - (b[1].time ?? 9999)
      })
    const dnfEntries = Object.entries(players).filter(([, p]) => p.status !== 'finished')
    const ranked = rankFinished(finishedSorted)
    const allRanked = [...ranked, ...dnfEntries.map(([uid, p]) => [uid, p, null])]
    const winner = ranked.find(([, , r]) => r === 1)
    const winnerStat = winner ? formatStat(winner[1], isSpeedRun) : null

    return (
      <div className="party-battle-screen party-battle-screen--results">
        <MiniConfetti />

        {winner ? (
          <div className="pb-winner-spotlight">
            <div className="pb-winner-spotlight__crown" aria-hidden="true">
              <IconCrown size={32} />
            </div>
            <h2 className="pb-winner-spotlight__name">
              {winner[1].username || 'Guest'} wins!
            </h2>
            <p className="pb-winner-spotlight__sub">
              {ModeIcon && <ModeIcon size={14} aria-hidden="true" />}
              <span>{MODE_NAMES[party.mode] || party.mode}</span>
              <span aria-hidden="true">·</span>
              <span>{winnerStat}</span>
            </p>
          </div>
        ) : (
          <h2 className="party-battle-screen__results-title">Game Over</h2>
        )}

        <div className="party-battle-screen__results-list">
          {allRanked.map(([uid, p, rank]) => {
            const isDnf = p.status !== 'finished'
            const isWinner = rank === 1
            const initial = (p.username || 'G').charAt(0).toUpperCase()
            return (
              <div
                key={uid}
                className={[
                  'party-battle-results-row',
                  isWinner ? 'party-battle-results-row--winner' : '',
                  isDnf ? 'party-battle-results-row--dnf' : '',
                ].join(' ').trim()}
              >
                <div className="party-battle-results-row__rank">
                  {isWinner ? (
                    <span className="pb-rank-crown" aria-label="1st place">
                      <IconCrown size={18} />
                    </span>
                  ) : rank != null ? (
                    `#${rank}`
                  ) : (
                    <span aria-hidden="true">—</span>
                  )}
                </div>
                <div className="party-battle-results-row__avatar">
                  {p.avatar
                    ? <img src={p.avatar} alt="" />
                    : <span>{initial}</span>}
                </div>
                <div className="party-battle-results-row__info">
                  <span className="party-battle-results-row__name">
                    {p.username || 'Guest'}
                  </span>
                  {p.status === 'finished' ? (
                    <span className="party-battle-results-row__stats">
                      {formatStat(p, isSpeedRun)}
                    </span>
                  ) : (
                    <span className="party-battle-results-row__stats party-battle-results-row__stats--dnf">
                      <IconX size={12} aria-hidden="true" />
                      <span>Did not finish</span>
                    </span>
                  )}
                </div>
                {p.chain && p.chain.length > 0 && (
                  <button
                    className="btn-ghost party-battle-results-row__chain-toggle"
                    onClick={() => setExpandedChain(expandedChain === uid ? null : uid)}
                  >
                    {expandedChain === uid ? 'Hide' : 'Chain'}
                  </button>
                )}
                {expandedChain === uid && p.chain && (
                  <div className="party-battle-results-row__chain">
                    <Filmstrip chain={p.chain} onItemClick={() => {}} hidePlaceholder />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className="party-battle-screen__results-actions">
          {onPlayAgain && (
            <button className="btn-primary pb-action-btn pb-action-btn--primary" onClick={onPlayAgain}>
              <IconReplay size={18} aria-hidden="true" />
              <span>Play Again</span>
            </button>
          )}
          {onBackToLobby && (
            <button className="btn-ghost pb-action-btn pb-action-btn--outline" onClick={onBackToLobby}>
              Back to Lobby
            </button>
          )}
          {onEndParty && (
            <button className="btn-ghost pb-action-btn pb-action-btn--muted" onClick={onEndParty}>
              End Party
            </button>
          )}
        </div>
      </div>
    )
  }

  // ── Live status board view ────────────────────────────────────────────────
  return (
    <div className="party-battle-screen">
      <div className="party-battle-screen__header">
        <div className="party-battle-screen__actors">
          <ActorThumb actor={party?.startActor} label="Start" />
          <span className="party-battle-screen__arrow" aria-hidden="true">
            <IconArrowRight size={20} />
          </span>
          <ActorThumb actor={party?.goalActor} label="Goal" />
        </div>

        <div className="party-battle-screen__timer">
          <span className="party-battle-screen__timer-label" aria-hidden="true">
            <IconClock size={20} />
          </span>
          <span className="party-battle-screen__timer-value">{fmtTime(elapsed)}</span>
        </div>

        <p className="party-battle-screen__progress">
          {finishedCount}/{playerCount} finished
        </p>
      </div>

      <div className="party-battle-screen__leaderboard">
        {statusBoard.map(([uid, p]) => {
          const initial = (p.username || 'G').charAt(0).toUpperCase()
          const rowClass = [
            'party-battle-row',
            p.status === 'finished' ? 'party-battle-row--finished' : '',
            p.status === 'dnf' ? 'party-battle-row--dnf' : '',
          ].join(' ').trim()
          return (
            <div key={uid} className={rowClass}>
              <div className="party-battle-row__avatar">
                {p.avatar
                  ? <img src={p.avatar} alt="" />
                  : <span>{initial}</span>}
              </div>
              <span className="party-battle-row__name">{p.username || 'Guest'}</span>
              <span className="party-battle-row__status">
                {p.status === 'finished' ? (
                  <span className="pb-status-chip pb-status-chip--finished">
                    <IconCheck size={12} aria-hidden="true" />
                    <span>{formatStat(p, isSpeedRun)}</span>
                  </span>
                ) : p.status === 'dnf' ? (
                  <span className="pb-status-chip pb-status-chip--dnf">
                    <IconX size={12} aria-hidden="true" />
                    <span>Did not finish</span>
                  </span>
                ) : (
                  <span className="pb-status-chip pb-status-chip--playing">
                    <span className="pb-shoot-dot" aria-hidden="true" />
                    <span>Still shooting</span>
                  </span>
                )}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ActorThumb({ actor, label }) {
  if (!actor) return null
  const initial = (actor.name || 'A').charAt(0).toUpperCase()
  return (
    <div className="party-battle-screen__actor-thumb">
      <div className="party-battle-screen__actor-img">
        {actor.profile_path
          ? <img src={profileUrl(actor.profile_path, 'w185')} alt={actor.name} />
          : <span className="party-battle-screen__actor-initial">{initial}</span>}
      </div>
      <span className="party-battle-screen__actor-label">{label}</span>
      <span className="party-battle-screen__actor-name">{actor.name}</span>
    </div>
  )
}
